"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSnapshotWhere } from "../../../hooks/useSnapshotWhere.js";
import { useSnapshotWhereTwo } from "../../../hooks/useSnapshotWhereTwo.js";
import useUpdateDoc from "../../../hooks/useUpdateDoc.js";
import useDeleteDoc from "../../../hooks/useDeleteDoc.js";
import { Item } from "../components/Table.js/ModalContainer.js";
import GenerateReportInvestor from "./generatePayroll.js";
import Spinner from "../../../components/Spinner/index.js";
import Modal from "../../../components/Modal/index.js";
import { formatNumber } from "../../../utils/format.js";
import { formatCurrencyTable } from "../../../utils/formatCurrencyTable.js";
import { planPagos } from "../../../utils/planPagos.js";
import { formatCurrency } from "../../../utils/formatCurrency.js";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../../../FirebaseConfig.ts";
import useSnapshotWithoutInvestor from "../../../hooks/useSnapshotWithoutInvestor.js";
import HistorialReportesInvestor from "./historyReport.js";
import dayjs from "dayjs";

import Trash from "../../../components/SVG/trash.js";

function Investor() {
  const { idInvestment } = useParams();
  const [isLoaded, setIsLoaded] = useState(false);
  const [totalIngresos, setTotalIngresos] = useState("");
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawType, setWithdrawType] = useState("");
  const [valueTextArea, setValueTextArea] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [selectedCredit, setSelectedCredit] = useState("");
  const [selectedCreditData, setSelectedCreditData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [investmentTasa, setInvestmentTasa] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [fechaPago, setFechaPago] = useState(dayjs().format("YYYY-MM-DD"));

  const navigate = useNavigate();

  const { data: investorRes, isFetching: loadingInvestor } = useSnapshotWhere(
    "investor",
    {
      filter: {
        firstCondition: "uuid",
        secondCondition: "==",
        thirdCondition: idInvestment,
      },
    }
  );

  const { data: reqRes, isFetching: loadingReq } = useSnapshotWhereTwo(
    "requests",
    {
      filter: {
        firstCondition: "investorId",
        secondCondition: "==",
        thirdCondition: idInvestment,
      },
      second: {
        firstCondition: "status",
        secondCondition: "==",
        thirdCondition: "aprobado",
      },
    }
  );

  const { data: reqResP } = useSnapshotWhereTwo("requests", {
    filter: {
      firstCondition: "investorId",
      secondCondition: "==",
      thirdCondition: idInvestment,
    },
    second: {
      firstCondition: "status",
      secondCondition: "==",
      thirdCondition: "pagado",
    },
  });

  // Consultar créditos sin inversionista con búsqueda debounced
  const {
    data: creditsWithoutInvestor,
    isFetching: loadingCreditsNoInvestor,
    isSearching,
  } = useSnapshotWithoutInvestor(searchTerm);

  // Consultar movimientos del inversionista
  const { data: movements, isFetching: loadingMovements } = useSnapshotWhere(
    "investorMovements",
    {
      filter: {
        firstCondition: "investorId",
        secondCondition: "==",
        thirdCondition: idInvestment,
      },
    }
  );

  const { handleUpdate } = useUpdateDoc();
  const { handleDelete } = useDeleteDoc();

  useEffect(() => {
    setIsLoaded(true);
    if (reqRes.length === 0) return;

    let totalIngresos = 0;

    reqRes.forEach((el) => {
      const { schedule } = planPagos(el, el?.tasaInvestor);
      const suma = schedule[0]?.cuotaAPagar || 0;
      totalIngresos += suma;
    });

    setTotalIngresos(totalIngresos);
  }, [reqRes.length]);

  const investor = investorRes[0];

  const [editing, setEditing] = useState(false);
  const [newBalance, setNewBalance] = useState(investor?.currentAmmount || 0);

  // Calcular métricas del inversionista
  const calculateInvestorMetrics = () => {
    if (!reqRes || reqRes.length === 0) {
      return {
        totalInvertido: 0,
        gananciaMensual: 0,
        gananciaTotal: 0,
        creditosActivos: 0,
        totalPagosRecibidos: 0,
        roi: 0,
      };
    }

    let totalInvertido = 0;
    let gananciaMensual = 0;
    let gananciaTotal = 0;
    let creditosActivos = 0;
    let totalPagosRecibidos = 0;

    reqRes.forEach((credito) => {
      if (credito.status === "aprobado") {
        creditosActivos++;
        totalInvertido += Number(credito.ammount) || 0;

        const tasaInvestor = Number(credito.tasaInvestor) || 0;
        const monto = Number(credito.ammount) || 0;
        const pagosRealizados = credito.paymentsInvestor?.length || 0;

        const gananciaCredito = (monto * tasaInvestor) / 100 / 12;
        gananciaMensual += gananciaCredito;

        gananciaTotal += gananciaCredito * pagosRealizados;

        totalPagosRecibidos += pagosRealizados;
      }
    });

    const roi = totalInvertido > 0 ? (gananciaTotal / totalInvertido) * 100 : 0;

    return {
      totalInvertido,
      gananciaMensual,
      gananciaTotal,
      creditosActivos,
      totalPagosRecibidos,
      roi,
    };
  };

  const metrics = calculateInvestorMetrics();

  const handleSave = async () => {
    await handleUpdate("investor", idInvestment, {
      currentAmmount: Number(newBalance),
    });
    setEditing(false);
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || Number(withdrawAmount) <= 0) {
      alert("Por favor ingresa un monto válido");
      return;
    }

    const amount = Number(withdrawAmount);
    const currentBalance = investor?.currentAmmount || 0;

    if (amount > currentBalance && withdrawType != "addMoney") {
      alert("El monto a retirar excede el saldo disponible");
      return;
    }

    if (withdrawType === "reinvest" && !selectedCredit) {
      alert("Por favor selecciona un crédito para reinvertir");
      return;
    }

    if (withdrawType === "reinvest" && !investmentTasa) {
      alert("Por favor selecciona una tasa para el inversionista");
      return;
    }

    setIsProcessing(true);

    try {
      let newBalance;

      if (withdrawType === "addMoney") {
        newBalance = currentBalance + amount;
      } else {
        newBalance = currentBalance - amount;
      }

      await handleUpdate("investor", idInvestment, {
        currentAmmount: newBalance,
      });

      if (withdrawType === "reinvest" && selectedCredit) {
        await handleUpdate("requests", selectedCredit, {
          investorId: idInvestment,
          tasaInvestor: Number(investmentTasa),
        });
      }

      const movementData = {
        investorId: idInvestment,
        investorName: investor.name,
        type: withdrawType === "bank" ? "retiro_banco" : "reinversion",
        amount: amount,
        detail: valueTextArea || "",
        creditId: withdrawType === "reinvest" ? selectedCredit : null,
        creditData: {
          name: selectedCreditData.name || "",
          lastName: selectedCreditData.lastName || "",
          identity: selectedCreditData.identity || "",
          numberCredit: selectedCreditData.numberCredit || "",
        },
        date: `${fechaPago}`,
        status: "completado",
        description:
          withdrawType === "bank"
            ? "Retiro a cuenta bancaria"
            : `Reinversión en crédito`,
      };

      await addDoc(collection(db, "investorMovements"), movementData);

      setShowWithdrawModal(false);
      setWithdrawAmount("");
      setWithdrawType("");
      setSelectedCredit("");
      setSelectedCreditData({});
      setSearchTerm("");
      setInvestmentTasa("");
      setValueTextArea("");
    } catch (error) {
      console.error("Error procesando retiro:", error);
      alert("Error al procesar la operación. Intenta de nuevo.");
    } finally {
      setIsProcessing(false);
    }
  };

  const getInvestorInitials = (name) => {
    return name
      ?.split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleDeleteRequest = async (movement) => {
    const confirmDelete = window.confirm(
      "¿Estás seguro de eliminar este movimiento?"
    );
    if (!confirmDelete) return;

    try {
      await handleDelete("investorMovements", movement.id);

      await handleUpdate("investor", idInvestment, {
        currentAmmount: Number(investor?.currentAmmount || 0) - movement.amount,
      });
    } catch (error) {
      console.error("Error eliminando la solicitud:", error);
    }
  };

  const getROIColor = (roi) => {
    if (roi >= 15) return "text-green-600";
    if (roi >= 10) return "text-blue-600";
    if (roi >= 5) return "text-yellow-600";
    return "text-gray-600";
  };

  const getMovementIcon = (type) => {
    if (type === "retiro_banco") {
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className="text-red-600"
        >
          <path
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    } else if (type === "reinversion") {
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          className="text-green-600"
        >
          <path
            d="M9 11l3-3m0 0l3 3m-3-3v8m0-13a9 9 0 110 18 9 9 0 010-18z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    return null;
  };

  if (loadingInvestor) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-4">
          <Spinner />
          <p className="text-gray-600">
            Cargando información del inversionista...
          </p>
        </div>
      </div>
    );
  }

  if (!investor) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            className="text-gray-400"
          >
            <path
              d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 7a4 4 0 108 0 4 4 0 00-8 0M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">
          No se encontró el inversionista
        </h3>
        <p className="text-gray-600">
          El inversionista solicitado no existe o no tienes permisos para verlo
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header Section */}
      <div
        className={`transition-all duration-700 ease-out ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-gradient-to-r from-[#49b0a8] to-[#3a8a84] rounded-2xl shadow-lg p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white font-bold text-2xl border-2 border-white/30">
              {getInvestorInitials(investor.name)}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2">
                {investor.name} {investor.lastName}
              </h1>
              <p className="text-white/90 text-lg">{investor.email}</p>
              {investor.phone && (
                <p className="text-white/80">{investor.phone}</p>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm text-white/80 mb-1">Total Invertido</div>
              <div className="text-3xl font-bold">
                {formatNumber(metrics?.totalInvertido?.toFixed(0) || 0)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Métricas Financieras */}
      <div
        className={`transition-all duration-700 ease-out delay-100 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Ganancia Mensual */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-white"
                >
                  <path
                    d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="px-3 py-1 bg-green-200 text-green-800 rounded-full text-xs font-semibold">
                +Mensual
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-green-700">Pago Mensual</p>
              <p className="text-2xl font-bold text-green-900">
                {formatNumber(totalIngresos ? totalIngresos?.toFixed(0) : 0)}
              </p>
              <p className="text-xs text-green-600">
                Por todos los créditos activos
              </p>
            </div>
          </div>

          {/* Ganancia Total Acumulada - CLICKEABLE */}
          <div
            onClick={() => setShowWithdrawModal(true)}
            className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200 p-6 cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-white"
                >
                  <path
                    d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-xs font-semibold">
                Total
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-700">Saldo Total</p>
              <p className="text-2xl font-bold text-blue-900">
                {formatNumber(investorRes[0]?.currentAmmount?.toFixed(0) || 0)}
              </p>
              <p className="text-xs text-blue-600 flex items-center justify-center gap-1 text-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 15l-6-6m0 0h4.5M9 9v4.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Clic para hacer retiros
              </p>
            </div>
          </div>

          {/* Créditos Activos */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm border border-purple-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-white"
                >
                  <path
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="px-3 py-1 bg-purple-200 text-purple-800 rounded-full text-xs font-semibold">
                Activos
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-purple-700">
                Créditos Activos
              </p>
              <p className="text-2xl font-bold text-purple-900">
                {metrics.creditosActivos}
              </p>
              <p className="text-xs text-purple-600">Total de inversiones</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div
        className={`grid grid-cols-1 gap-6 transition-all duration-700 ease-out delay-200 bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        {/* Investor Information */}
        <div className={`grid grid-cols-2 gap-6 `}>
          <div className="">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 3a4 4 0 100 8 4 4 0 000-8z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Información del Inversionista
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1 p-4 bg-gray-50 rounded-lg">
                  <label className="text-xs font-medium text-gray-500">
                    Total de Pagos Recibidos
                  </label>
                  <p className="text-2xl font-bold text-gray-900">
                    {metrics.totalPagosRecibidos}
                  </p>
                </div>
                <div className="space-y-1 p-4 bg-gray-50 rounded-lg">
                  <label className="text-xs font-medium text-gray-500">
                    Créditos
                  </label>
                  <p className="text-2xl font-bold text-gray-900">
                    {metrics.creditosActivos}
                  </p>
                </div>
              </div>

              {investor.company && (
                <div className="space-y-1 p-4 bg-gray-50 rounded-lg">
                  <label className="text-sm font-medium text-gray-600">
                    Empresa
                  </label>
                  <p className="text-gray-900">{investor.company}</p>
                </div>
              )}

              {investor.documents && (
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-600 mb-3">
                    Documentos del Inversionista
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {investor.documents.map((doc, index) => (
                      <Item key={index} href={doc.url} name={doc.name} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Generate Report */}
          <div>
            <div className="bg-gray-50 rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2 justify-center">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-[#49b0a8]"
                >
                  <path
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Generar Reporte de Inversión
              </h2>

              <div className="text-center">
                <GenerateReportInvestor
                  saldo={
                    investorRes[0]?.currentAmmount
                      ? formatCurrency(
                          investorRes[0]?.currentAmmount?.toFixed(0) || 0
                        )
                      : 0
                  }
                  investor={investor}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Créditos del inversionista */}
      <div
        className={`transition-all duration-700 ease-out delay-500 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Créditos de este inversionista
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {metrics.creditosActivos} crédito(s) activo(s) •{" "}
              {metrics.totalPagosRecibidos} pago(s) total(es) recibido(s)
            </p>
          </div>

          {loadingReq ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <Spinner />
                <p className="text-gray-600">Cargando créditos...</p>
              </div>
            </div>
          ) : reqRes?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-gray-400"
                >
                  <path
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                No hay créditos registrados
              </h3>
              <p className="text-gray-600">
                Aún no tiene créditos de inversión
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Tasa Crédito
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Tasa Inversor
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Pagos Realizados
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reqRes?.map((el, index) => {
                    const monto = Number(el.ammount) || 0;
                    const pagosRealizados = el.paymentsInvestor?.length || 0;

                    return (
                      <tr
                        onClick={() =>
                          navigate(
                            `/plataforma/simulador/${el.identity}/${el.uuid}?i=${idInvestment}`
                          )
                        }
                        key={el.uuid}
                        className={` transition-colors duration-200 cursor-pointer ${
                          el.status === "pagado"
                            ? "bg-gray-200"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {el.name} {el.lastName}
                            <br />{" "}
                            <span className="text-sm text-gray-600">
                              C.C. {el.identity}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">
                            ${formatCurrencyTable(monto || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {el.tasa}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {el.tasaInvestor}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {el.status === "pagado" ? "Pagado" : "Aprobado"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-[#49b0a8]">
                              {pagosRealizados}
                            </span>
                            <span className="text-xs text-gray-500">
                              / {el.month}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                            <div
                              className="bg-[#49b0a8] h-1.5 rounded-full transition-all duration-300"
                              style={{
                                width: `${(pagosRealizados / el.month) * 100}%`,
                              }}
                            ></div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#49b0a8] hover:bg-[#3a8a84] text-white rounded-lg text-sm font-medium transition-colors duration-200">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Ver Detalles
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Créditos pagados del  inversionista */}
      <div
        className={`transition-all duration-700 ease-out delay-500 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Créditos pagados de este inversionista
            </h2>
          </div>

          {loadingReq ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <Spinner />
                <p className="text-gray-600">Cargando créditos...</p>
              </div>
            </div>
          ) : reqResP?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-gray-400"
                >
                  <path
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                No hay créditos registrados
              </h3>
              <p className="text-gray-600">
                Aún no tiene créditos de inversión
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Tasa Crédito
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Tasa Inversor
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reqResP?.map((el, index) => {
                    const monto = Number(el.ammount) || 0;

                    return (
                      <tr
                        onClick={() =>
                          navigate(
                            `/plataforma/simulador/${el.identity}/${el.uuid}?i=${idInvestment}`
                          )
                        }
                        key={el.uuid}
                        className={` transition-colors duration-200 cursor-pointer ${
                          el.status === "pagado"
                            ? "bg-gray-200"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {el.name} {el.lastName}
                            <br />{" "}
                            <span className="text-sm text-gray-600">
                              C.C. {el.identity}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900">
                            ${formatCurrencyTable(monto || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {el.tasa}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {el.tasaInvestor}%
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#49b0a8] hover:bg-[#3a8a84] text-white rounded-lg text-sm font-medium transition-colors duration-200">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            Ver Detalles
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <HistorialReportesInvestor investorId={idInvestment} />

      {/* Tabla de Movimientos */}
      <div
        className={`transition-all duration-700 ease-out delay-600 ${
          isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
      >
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                className="text-[#49b0a8]"
              >
                <path
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Historial de Movimientos
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Retiros y reinversiones realizadas
            </p>
          </div>

          {loadingMovements ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center space-y-4">
                <Spinner />
                <p className="text-gray-600">Cargando movimientos...</p>
              </div>
            </div>
          ) : !movements || movements?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-gray-400"
                >
                  <path
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                No hay movimientos registrados
              </h3>
              <p className="text-gray-600">
                Aún no se han realizado retiros ni reinversiones
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Descripción
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {movements
                    ?.sort((a, b) => b.date - a.date)
                    ?.map((movement, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {getMovementIcon(movement.type)}
                            <span className="text-sm font-medium text-gray-900 capitalize">
                              {movement.type?.replace("_", " ")}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`text-sm font-bold ${
                              movement.type === "retiro_banco" ||
                              movement.type === "egreso_pago"
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                          >
                            {movement.type === "retiro_banco" ||
                            movement.type === "egreso_pago"
                              ? "-"
                              : "+"}
                            {formatNumber(movement.amount || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">
                            {movement.description} - {movement.detail}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {movement.date}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {movement.status}
                          </span>
                          <button
                            onClick={() => handleDeleteRequest(movement)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            <Trash size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Retiros */}
      <Modal
        showModal={showWithdrawModal}
        setShowModal={() => {
          setShowWithdrawModal(false);
          setSearchTerm("");
        }}
      >
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                className="text-blue-600"
              >
                <path
                  d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Gestión de Saldo
            </h3>
            <div className="flex justify-center items-center gap-2">
              {!editing ? (
                <div className="flex gap-2">
                  <p className="text-gray-600">
                    Saldo disponible:{" "}
                    {formatNumber(investor?.currentAmmount || 0)}
                  </p>
                  <button
                    onClick={() => setEditing(true)}
                    className="text-blue-500 hover:text-blue-700"
                    title="Editar saldo"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-4 h-4"
                    >
                      <path d="M17.414 2.586a2 2 0 0 0-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 0 0 0-2.828zM5 15a1 1 0 0 0-1 1v1h1a1 1 0 0 0 1-1v-1H5z" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={newBalance}
                    onChange={(e) => setNewBalance(e.target.value)}
                    className="border rounded px-2 py-1 w-28 text-sm"
                  />
                  <button
                    onClick={handleSave}
                    className="bg-green-500 text-white px-2 py-1 rounded text-sm hover:bg-green-600"
                  >
                    Guardar
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="bg-gray-300 text-gray-800 px-2 py-1 rounded text-sm hover:bg-gray-400"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Monto a retirar */}
            <div className="flex justify-between gap-3">
              <div className="w-full">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Monto a gestionar
                  </label>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder="Ingresa el monto"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                  />
                </div>
              </div>
              <div className="w-full">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Fecha
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={fechaPago}
                    onChange={(e) => setFechaPago(e.target.value)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  />
                </div>
              </div>
            </div>

            {/* Tipo de operación */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Tipo de operación
              </label>
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={() => setWithdrawType("bank")}
                  className={`p-4 border-2 rounded-xl transition-all duration-200 ${
                    withdrawType === "bank"
                      ? "border-[#49b0a8] bg-[#49b0a8]/10"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-[#49b0a8]"
                    >
                      <path
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-900">
                      Retiro al Banco
                    </span>
                  </div>
                </button>

                <button
                  onClick={() => setWithdrawType("reinvest")}
                  className={`p-4 border-2 rounded-xl transition-all duration-200 ${
                    withdrawType === "reinvest"
                      ? "border-[#49b0a8] bg-[#49b0a8]/10"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-[#49b0a8]"
                    >
                      <path
                        d="M9 11l3-3m0 0l3 3m-3-3v8m0-13a9 9 0 110 18 9 9 0 010-18z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-900">
                      Reinvertir
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setWithdrawType("addMoney")}
                  className={`p-4 border-2 rounded-xl transition-all duration-200 ${
                    withdrawType === "addMoney"
                      ? "border-[#49b0a8] bg-[#49b0a8]/10"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="-0.5 0 25 25"
                      className="text-[#49b0a8]"
                    >
                      <path
                        fill="currentColor"
                        d="M12.87 16.97v1.1a.67.67 0 0 1-1.34 0v-1.13a3.35 3.35 0 0 1-2.21-1.4.61.61 0 0 1-.108-.662.64.64 0 0 1 .618-.388.58.58 0 0 1 .45.21c.32.418.754.734 1.25.91v-2.54c-1.33-.53-2-1.3-2-2.31a2.27 2.27 0 0 1 2-2.17V7.48a.67.67 0 1 1 1.34 0v1.1a2.8 2.8 0 0 1 1.87 1.16.74.74 0 0 1 .15.4.64.64 0 0 1-.64.66.63.63 0 0 1-.47-.22 2.1 2.1 0 0 0-.91-.66v2.25l.25.1c1.27.49 2.06 1.21 2.06 2.36a2.41 2.41 0 0 1-2.31 2.34m-1.34-5.38V9.97a.82.82 0 0 0-.54.79.93.93 0 0 0 .54.8zm2.2 3.07c0-.49-.34-.78-.86-1v2a1 1 0 0 0 .86-1.03z"
                      ></path>
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M12.58 3.97H6a4 4 0 0 0-4 4v10a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V11.9M22 2.92l-5.66 5.66"
                      ></path>
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M20.87 8.58h-4.53V4.05"
                      ></path>
                    </svg>
                    <span className="text-sm font-medium text-gray-900">
                      Agregar Dinero
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {/* Selección de crédito para reinversión */}
            {withdrawType === "reinvest" ? (
              <div className="">
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Selecciona un crédito para reinvertir
                    </label>

                    {/* Input de búsqueda con indicador de búsqueda */}
                    <div className="relative">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar por nombre, cédula o número de crédito..."
                        className="w-full px-4 py-3 pl-10 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                      />
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                      >
                        <path
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {isSearching && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#49b0a8]"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Escribe la tasa para el inversionista
                    </label>

                    <div className="relative">
                      <input
                        type="text"
                        value={investmentTasa}
                        onChange={(e) => {
                          const formattedValue = e.target.value.replace(
                            /,/g,
                            "."
                          ); // 👈 reemplaza todas las comas
                          setInvestmentTasa(formattedValue);
                        }}
                        placeholder="Ej: 20.55"
                        className="w-full px-4 py-3 pl-10 pr-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#49b0a8] focus:border-[#49b0a8] transition-colors duration-200"
                      />
                    </div>
                  </div>
                </div>

                {loadingCreditsNoInvestor ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner />
                  </div>
                ) : creditsWithoutInvestor?.length === 0 && !searchTerm ? (
                  <div className="text-center py-6 bg-gray-50 rounded-xl">
                    <p className="text-gray-600">
                      No hay créditos disponibles para reinvertir
                    </p>
                  </div>
                ) : creditsWithoutInvestor?.length === 0 && searchTerm ? (
                  <div className="text-center py-6 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 mx-auto bg-gray-200 rounded-full flex items-center justify-center mb-3">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="text-gray-400"
                      >
                        <path
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-600 font-medium">
                      No se encontraron resultados
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Intenta con otro término de búsqueda
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        {creditsWithoutInvestor?.length} crédito(s){" "}
                        {searchTerm ? "encontrado(s)" : "disponible(s)"}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="text-xs text-[#49b0a8] hover:text-[#3a8a84] font-medium"
                        >
                          Limpiar búsqueda
                        </button>
                      )}
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-300 rounded-xl p-3">
                      {creditsWithoutInvestor?.map((credit) => (
                        <button
                          key={credit.uuid}
                          onClick={() => {
                            setSelectedCredit(credit.uuid);
                            setSelectedCreditData(credit);
                          }}
                          className={`w-full p-3 text-left border-2 rounded-lg transition-all duration-200 ${
                            selectedCredit === credit.uuid
                              ? "border-[#49b0a8] bg-[#49b0a8]/10"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">
                                {credit.name} {credit.lastName}
                              </p>
                              <p className="text-sm text-gray-600">
                                C.C. {credit.identity}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                ID: {credit.uuid?.slice(0, 8)}...
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-[#49b0a8]">
                                {formatNumber(credit.ammount)}
                              </p>
                              <p className="text-xs text-gray-600">
                                {credit.month} meses
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <textarea
                value={valueTextArea}
                onChange={(e) => setValueTextArea(e.target.value)}
                placeholder={"Descripción adicional (opcional)..."}
                rows={3}
                className={
                  "w-full min-h-[64px] resize-none px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-500 shadow-sm transition-all "
                }
              />
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowWithdrawModal(false);
                setSearchTerm("");
              }}
              disabled={isProcessing}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleWithdraw}
              disabled={
                isProcessing ||
                !withdrawAmount ||
                !withdrawType ||
                (withdrawType === "reinvest" && !selectedCredit)
              }
              className={`
                flex-1 px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2
                ${
                  isProcessing ||
                  !withdrawAmount ||
                  !withdrawType ||
                  (withdrawType === "reinvest" && !selectedCredit)
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#49b0a8] hover:bg-[#3a8a84] text-white"
                }
              `}
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Procesando...</span>
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Confirmar Operación</span>
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Investor;
